<template>
  <div>
    <section class="relative isolate overflow-hidden pt-24 pb-12 sm:pt-32 sm:pb-16">
      <shape-field />

      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <div
          data-animate-group
          class="mx-auto max-w-3xl text-center"
        >
          <atomic-eyebrow label="Retours d'expérience" />

          <h1 class="mt-6 text-balance text-5xl font-display font-bold tracking-tight text-gray-900 sm:text-6xl">
            Témoignages clients de nos solutions d'écrans en Suisse
          </h1>

          <p
            data-animate="fade-up"
            class="mt-6 text-pretty text-lg/8 text-gray-600"
          >
            Des établissements suisses expliquent quels contenus ils diffusent sur leurs écrans, comment ils les mettent à jour et ce que cela a changé dans leur quotidien.
          </p>

          <div
            data-animate="fade-up"
            class="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <button-primary
              link="/demo"
              label="Demander une démo"
              size="lg"
            />
            <button-secondary
              link="/cas-usages"
              label="Voir les cas d'usages"
              size="lg"
              icon-name="ph:arrow-right"
            />
          </div>
        </div>
      </div>
    </section>

    <section class="pt-12 pb-24 sm:pt-16 sm:pb-32">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <div
          data-animate-group
          class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          <nuxt-link
            v-for="testimonial in testimonials"
            :key="testimonial.slug"
            :to="`/temoignages/${testimonial.slug}`"
            data-animate="fade-up"
            data-spotlight
            class="group relative isolate flex h-full flex-col overflow-hidden rounded-3xl bg-white ring-1 ring-gray-200 active:ring-brand-200 shadow-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
          >
            <shape-spotlight />

            <atomic-card-media
              :image="testimonial.heroMedia.src"
              :image-alt="testimonial.heroMedia.alt"
              sizes="xs:480px sm:640px lg:520px"
              class="h-52"
            >
              <atomic-badge
                :label="testimonial.sector"
                class="absolute left-4 top-4 z-10 shadow-float"
              />
            </atomic-card-media>

            <div class="flex flex-1 flex-col gap-y-3 p-6 sm:p-8">
              <h2 class="text-xl font-display font-semibold tracking-tight text-gray-900">
                {{ testimonial.company }}
              </h2>

              <p class="text-base/7 text-gray-600">
                {{ testimonial.headline }}
              </p>

              <div class="mt-2 flex items-center gap-x-2 text-sm text-gray-500">
                <icon name="ph:map-pin-duotone" aria-hidden="true" class="size-4 flex-none text-brand-700" />
                {{ testimonial.location }}
              </div>

              <span class="mt-auto inline-flex items-center gap-x-1.5 pt-4 text-sm font-semibold text-brand-600 transition group-hover:text-brand-700">
                Lire le témoignage
                <icon name="ph:arrow-right" aria-hidden="true" class="size-4 transition group-hover:translate-x-0.5" />
              </span>
            </div>
          </nuxt-link>
        </div>
      </div>
    </section>

    <section-cta />

    <section-related-links :links="relatedLinks" />
  </div>
</template>

<script setup lang="ts">
const config = useRuntimeConfig()
const env = config.public
const baseUrl = env.PUBLIC_URL.replace(/\/+$/, '')

const relatedLinks = getCrossLinks(['casUsages', 'fonctionnalites', 'tarifs'])

useSeo({
  title: `Témoignages clients de l'affichage dynamique en Suisse — ${appName}`,
  description: 'Des établissements suisses racontent leur usage de Live Display : bars, restaurants, commerces et plus. Usages concrets, résultats et retours d\'expérience.',
  path: '/temoignages',
  jsonLd: [
    getBreadcrumbJsonLd(baseUrl, [
      { name: 'Accueil', path: '/' },
      { name: 'Témoignages', path: '/temoignages' }
    ]),
    getTestimonialListJsonLd(baseUrl)
  ]
})
</script>
